import { hasRecoveryData, saveRecoveryData } from "../data";
import { AdBlockerType, SoftwallRecoveredType } from "../types";
import { appendKVPToAdRequest, sendEvent, isSessionRecovered } from "../utils";
import { log, shouldLogByProbability } from "../utils/logs";
import { ImpressionsCounter } from "../utils/impressionsCounter";

const INACTIVITY_TIMEOUT = 60 * 1000;

function initAdTrackingEventDispatcher(
  impressions: ImpressionsCounter,
  recovered: SoftwallRecoveredType,
  detectedExtension: AdBlockerType,
) {
  const sendAdTrackingEvent = () => {
    const impressionsCount = impressions.getImpressionsCount();
    impressions.resetCounter(); // reset counter once we retrieve data

    // avoid sent event twice
    if (window.__bt_tag_am.kvpEventSent) {
      return;
    }

    sendEvent({
      event: "recovered-ads",
      meta: {
        impressionsCount,
      },
      recoveryType: recovered,
      detectedExtension,
    }).catch(() => null);

    window.__bt_tag_am.kvpEventSent = true;
  };

  // on page unload
  const unloadListener = () => {
    // trick to be able test ad tracking event
    // beforeunload happen on tab close or refresh, so listener expect to be removed;
    sendAdTrackingEvent();
    window.removeEventListener("beforeunload", unloadListener);
  };
  window.addEventListener("beforeunload", unloadListener);

  // on tab visibility changes
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      sendAdTrackingEvent();
    }
  });

  // reset eventSent when new impressions appear
  const watchNewImpressions = () => {
    setInterval(() => {
      const impressionsCount = impressions.getImpressionsCount();
      if (window.__bt_tag_am.kvpEventSent && impressionsCount > 0) {
        window.__bt_tag_am.kvpEventSent = false; // Allow another event to be sent
      }
    }, 5000);
  };

  watchNewImpressions();

  let inactivityTimer: ReturnType<typeof setTimeout>;

  const resetInactivityTimer = () => {
    if (!window.__bt_tag_am.kvpEventSent) {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        sendAdTrackingEvent();
      }, INACTIVITY_TIMEOUT);
    }
  };

  // Reset inactivity timer on user activity
  ["mousemove", "keydown", "scroll", "click"].forEach((event) => {
    document.addEventListener(event, resetInactivityTimer);
  });

  resetInactivityTimer();
}

export function onRecover(
  recovered: SoftwallRecoveredType,
  attributionExpiry: number,
) {
  if (isSessionRecovered(recovered, attributionExpiry)) {
    appendKVPToAdRequest();
    // Start tracking impressions
    ImpressionsCounter.getInstance();

    // keep log for now, but only for 1% of page loads
    if (shouldLogByProbability()) {
      log(
        "rlink.appendKVP",
        {
          aaResults: window.__bt_intrnl.aaDetectionResults,
          attributionExpiry,
          consentData: window.__bt_intrnl.consentData,
        },
        { severity: 1 },
      );
    }
  }
}

export async function onRecoverEvent(
  recovered: SoftwallRecoveredType,
  attributionExpiry: number,
  detectedExtension: AdBlockerType,
): Promise<void> {
  // gt singleton counter and init event tracking
  const impressionsCounter = ImpressionsCounter.getInstance();
  initAdTrackingEventDispatcher(
    impressionsCounter,
    recovered,
    detectedExtension,
  );

  if (!(await hasRecoveryData())) {
    sendEvent({
      event: "recovered",
      recoveryType: recovered,
      detectedExtension,
    }).catch(() => null);

    return saveRecoveryData(recovered, attributionExpiry);
  }
}
